import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { BudgetForm } from "@/components/admin/commercial/budget-form";
import { ServiceItemForm } from "@/components/admin/commercial/service-item-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Pencil, Plus } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/format";
import { useToast } from "@/hooks/use-toast";

interface BudgetDetail {
  id: string;
  name: string;
  revenueCategory: string;
  startDate: string;
  endDate: string | null;
  budgetedMinutes: number | null;
  budgetedAmount: string | null;
  currency: string;
  notes: string | null;
  contract: { id: string; name: string } | null;
  service?: { id: string; customName?: string | null; product?: { name: string } } | null;
  client: { id: string; name: string };
  periods: Array<{ id: string; periodStart: string; periodEnd: string; plannedMinutes: number | null; plannedAmount: string | null }>;
  serviceItems: Array<{ id: string; name: string; billingMode: string; serviceType: { label: string } }>;
}

export default function BudgetDetailPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [, params] = useRoute("/budgets/:id");
  const budgetId = params?.id;
  const [editOpen, setEditOpen] = useState(false);
  const [itemOpen, setItemOpen] = useState(false);

  const { data: budget, isLoading } = useQuery<BudgetDetail>({
    queryKey: ["/api/budgets", budgetId],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!budgetId,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const response = await apiRequest("PATCH", `/api/budgets/${budgetId}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/budgets"] });
      queryClient.invalidateQueries({ queryKey: ["/api/budgets", budgetId] });
      setEditOpen(false);
      toast({ title: "Budget updated" });
    },
  });

  const createItemMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const response = await apiRequest("POST", "/api/service-items", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/budgets", budgetId] });
      queryClient.invalidateQueries({ queryKey: ["/api/service-items"] });
      setItemOpen(false);
      toast({ title: "Service item created" });
    },
  });

  const periodColumns: ColumnDef<BudgetDetail["periods"][number]>[] = [
    {
      key: "period",
      header: "Period",
      cell: (row) => `${formatDate(row.periodStart)} to ${formatDate(row.periodEnd)}`,
    },
    {
      key: "plannedMinutes",
      header: "Planned minutes",
      cell: (row) => row.plannedMinutes ?? "—",
    },
    {
      key: "plannedAmount",
      header: "Planned amount",
      cell: (row) => (row.plannedAmount ? formatCurrency(row.plannedAmount, budget?.currency) : "—"),
    },
  ];

  const itemColumns: ColumnDef<BudgetDetail["serviceItems"][number]>[] = [
    {
      key: "name",
      header: "Service item",
      cell: (row) => row.name,
    },
    {
      key: "serviceType",
      header: "Service type",
      cell: (row) => row.serviceType.label,
    },
    {
      key: "billingMode",
      header: "Billing mode",
      cell: (row) => row.billingMode.replace(/_/g, " "),
    },
  ];

  if (!budgetId) return null;

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate("/budgets")}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to budgets
      </Button>

      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{budget?.name ?? "Budget"}</h1>
          <p className="text-sm text-muted-foreground">
            {(budget?.contract?.name || budget?.service?.customName || budget?.service?.product?.name || "Standalone")} · {budget?.client.name}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)} disabled={!budget}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>
          <Button onClick={() => setItemOpen(true)} disabled={!budget}>
            <Plus className="mr-2 h-4 w-4" />
            Add Service Item
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Budget Summary</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-4">
          <div>
            <p className="text-xs text-muted-foreground">Revenue</p>
            <p>{budget?.revenueCategory.replace(/_/g, " ") ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Minutes</p>
            <p>{budget?.budgetedMinutes ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Amount</p>
            <p>{budget?.budgetedAmount ? formatCurrency(budget.budgetedAmount, budget.currency) : "—"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Dates</p>
            <p>{budget ? `${formatDate(budget.startDate)} to ${budget.endDate ? formatDate(budget.endDate) : "open"}` : "—"}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Budget Periods</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={periodColumns}
            data={budget?.periods}
            isLoading={isLoading}
            rowKey={(row) => row.id}
            emptyTitle="No periods"
            emptyDescription="Periods are generated from the budget recurrence."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Service Items</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={itemColumns}
            data={budget?.serviceItems}
            isLoading={isLoading}
            rowKey={(row) => row.id}
            emptyTitle="No service items"
            emptyDescription="Each budget should be broken into service items before downstream work starts."
          />
        </CardContent>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Budget</DialogTitle>
          </DialogHeader>
          {budget ? (
            <BudgetForm
              fixedContractId={budget.contract?.id}
              defaultValues={{
                contractId: budget.contract?.id ?? "",
                serviceId: budget.service?.id ?? "",
                name: budget.name,
                startDate: budget.startDate.slice(0, 10),
                endDate: budget.endDate?.slice(0, 10) ?? "",
                revenueCategory: budget.revenueCategory as any,
                budgetedMinutes: budget.budgetedMinutes ? String(budget.budgetedMinutes) : "",
                budgetedAmount: budget.budgetedAmount ?? "",
                currency: budget.currency,
                notes: budget.notes ?? "",
              }}
              onSubmit={(data) => updateMutation.mutate(data)}
              isPending={updateMutation.isPending}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={itemOpen} onOpenChange={setItemOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Service Item</DialogTitle>
          </DialogHeader>
          <ServiceItemForm
            fixedBudgetId={budgetId}
            onSubmit={(data) => createItemMutation.mutate(data)}
            isPending={createItemMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
